import dayjs from 'dayjs';

import { LoginType } from '@/models/LoginType';
import StorageService from '@/services/StorageService';
import emailProviderBlacklist from '@/utils/data/emailProviderBlacklist';
import { cleanDoc } from '@/helpers/formatters';

interface ValidationResultOk {
  isValid: true;
  errorCode?: undefined;
}

interface ValidationResultErr<T> {
  isValid: false;
  errorCode: T;
}

export type ValidationResult<T> = ValidationResultOk | ValidationResultErr<T>

const makeErr = <T>(err: T): ValidationResult<T> => ({ isValid: false, errorCode: err });

type UserameErrorCode = 'invalidChars' | 'tooShort';

export function validateUsername(
  username: string,
  userType: LoginType,
): ValidationResult<UserameErrorCode> {
  if (userType === 'cpf') {
    const terms = username.split(' ');

    if (terms.length < 2) {
      // nome deve ter ao menos 2 termos
      return makeErr('tooShort');
    }

    if (terms.length === 2) {
      // para nomes com apenas 2 termos:
      // — cada termo deve possuir ao menos 2 caracteres
      // — total de caracteres imprimíveis deve ser maior ou igual a 5
      const hasSingleLetterTerm = terms.some((t) => t.length < 2);
      const spaces = terms.length - 1;
      const hasAtLeast5Letters = (username.length - spaces) >= 5;

      if (hasSingleLetterTerm || !hasAtLeast5Letters) return makeErr('tooShort');
    }

    const hasNonAlphaChars = /[^a-zàáâãäçèéêëìíîïñòóôõöùúûüýÿ' -]/.test(username.toLowerCase());
    if (hasNonAlphaChars) {
      // nome não pode conter caracteres especiais
      return makeErr('invalidChars');
    }
  } else if (username.length <= 5) {
    // nome de pessoa jurídica deve ter ao menos 5 caracteres
    return makeErr('tooShort');
  }

  return { isValid: true };
}

export function validatePhone(txt: string): boolean {
  const digits: string = txt.replace(/\D+/g, '');
  return /^[1-9]{2,3}\d{8}$/.test(digits);
}

export function validateBirthday(dataNascimento: string): boolean {
  const date = dayjs(dataNascimento, 'DD/MM/YYYY', true);
  return date.isValid() && date.isBefore(dayjs());
}

export function validateEmail(email: string): boolean {
  const emailProvider = email.split('@')[1];
  const isEmailProviderAllowed = !!StorageService.disableEmailProviderBlackList.get()
    || !emailProviderBlacklist.has(emailProvider);

  return /^((?!\.)[^\s@]+)(@[^\s@]+\.[^\s@]+)$/.test(email) && isEmailProviderAllowed;
}

function validateDV(txt: string | undefined, size: number, maxFactor: number) {
  if (!txt) return false;

  const digits = Array.from(txt.replace(/\D+/g, ''))
    .map((d) => parseInt(d, 10))
    .reverse();

  if (digits.length !== size || new Set(digits).size === 1) {
    return false;
  }

  const calcDV = (nums: number[]) => {
    const modFactor = maxFactor - 1;

    let sum = 0;
    for (let i = 0; i < nums.length; i += 1) {
      const factor = 2 + (i % modFactor);
      sum += nums[i] * factor;
    }

    return (sum * 10) % 11 % 10;
  };

  const checkDv = (n: number) => digits[n] === calcDV(digits.slice(n + 1));

  return checkDv(1) && checkDv(0);
}

export function validateCpf(txt?: string): boolean {
  return validateDV(txt, 11, 11);
}

export function validateCnpj(txt?: string): boolean {
  return validateDV(txt, 14, 9);
}

/**
 * Celular estrito (11 dígitos). `validatePhone` aceita fixo (10 dígitos) e não serve onde o
 * contrato exige celular.
 */
export function validateCelular(value: string): boolean {
  return cleanDoc(value).length === 11;
}

export function validateCep(value: string): boolean {
  return cleanDoc(value).length === 8;
}

export function validateUf(value: string): boolean {
  return /^[A-Z]{2}$/.test(value.trim());
}

export default {
  username: validateUsername,
  phone: validatePhone,
  email: validateEmail,
  cpf: validateCpf,
  cnpj: validateCnpj,
  birthdate: validateBirthday,
  celular: validateCelular,
  cep: validateCep,
  uf: validateUf,
};
